import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { INTERNAL_PROVIDERS } from '../../../../constants/providers';
import { fetchStagingDistinctValues } from '../../../../services/queries/FileIngestionQueries';
import {
  SnowflakeColumnAutocomplete,
  useSnowflakeColumnOptions,
} from '../components/SnowflakeColumnAutocomplete';
import { KPI_PERIODS, KPI_SECTIONS } from '../constants';
import { FieldAutomationDataType } from '../../../../services/queries/FileIngestionQueries';
import { dataTypeHasPeriod } from '../data-type';

/**
 * Row Layout control, shared by the file and Snowflake sections. Pure physical layout —
 * one row vs multiple rows per entity (+ "first row only"). Only rendered for Entity
 * Properties configs: financials/commentary rows are entity-period by definition, so
 * layout is not a choice there (write-through sets rowStructure for them on save).
 */
function RowLayoutFields() {
  const { control, watch } = useFormContext();
  const rowStructure = watch('config.fileLevel.rowStructure');

  // A leftover periodic value ('row-per-entity-period') is normalized in the DISPLAY only
  // (value prop below) — deliberately not written back to form state on mount, which would
  // dirty the form from merely viewing the tab (spurious Update enable + "Discard Changes?"
  // on close for untouched legacy configs). The save-chokepoint write-through normalizes
  // rowStructure on actual save, and both in-form consumers of the raw value (row-filtering
  // and first-row-only gates) compare against 'multiple-rows-per-entity', where the stale
  // value and the displayed value behave identically.
  return (
    <>
      <Controller
        name='config.fileLevel.rowStructure'
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            // Display-only normalization of legacy periodic values (see comment above).
            value={field.value === 'multiple-rows-per-entity' ? field.value : 'row-per-entity'}
            select
            label='Row Layout'
            fullWidth
            helperText='How rows in the source relate to entities.'
          >
            <MenuItem value='row-per-entity'>One row per entity</MenuItem>
            <MenuItem value='multiple-rows-per-entity'>Multiple rows per entity</MenuItem>
          </TextField>
        )}
      />
      {rowStructure === 'multiple-rows-per-entity' && (
        <Controller
          name='config.fileLevel.firstRowOnly'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox checked={field.value ?? false} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label='Use first row only per entity'
            />
          )}
        />
      )}
    </>
  );
}

export function IngestionSettingsSection() {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext();
  const sourceType = watch('config.sourceType') || 'file';
  const isApiIntegration = sourceType === 'api-integration';
  const isSnowflake = sourceType === 'snowflake';
  const isFileOnly = sourceType === 'file';
  const isFileOrSnowflake = isFileOnly || isSnowflake;
  const lookupStrategy = watch('config.entityMapping.lookupStrategy');
  const targetEntity = watch('config.entityMapping.targetEntity');
  const sourceFormat = watch('config.fileLevel.sourceFormat');
  const integrationProvider = watch('config.integrationProvider') as string | undefined;

  // Data Type (FD-3697) drives all conditional sections. The distinguishing axis is
  // "has a period?" — financials and commentary carry a reporting period; entity
  // properties never do (a dated custom field is still entity properties).
  const dataType = (watch('config.dataType') as FieldAutomationDataType | undefined) ?? 'entity-properties';
  const isEntityProperties = dataType === 'entity-properties';
  const isFinancials = dataType === 'company-financials';
  const hasPeriod = dataTypeHasPeriod(dataType);

  // For Snowflake, column-name fields render as autocompletes populated from the
  // configured Snowflake table. For files we keep the freeform text input.
  const snowflakeColumns = useSnowflakeColumnOptions();

  const { data: distinctValues } = useQuery({
    queryKey: ['staging-distinct-values', integrationProvider],
    queryFn: () => fetchStagingDistinctValues(integrationProvider!),
    enabled: isApiIntegration && !!integrationProvider,
  });

  const {
    fields: filenameFields,
    append: appendFilename,
    remove: removeFilename,
  } = useFieldArray({
    control,
    name: 'config.detection.filenamePatterns',
  });

  const {
    fields: s3PathFields,
    append: appendS3Path,
    remove: removeS3Path,
  } = useFieldArray({
    control,
    name: 'config.detection.s3PathPatterns',
  });

  const {
    fields: periodFields,
    append: appendPeriod,
    remove: removePeriod,
  } = useFieldArray({ control, name: 'config.periodLengthMappings' });

  const {
    fields: scenarioFields,
    append: appendScenario,
    remove: removeScenario,
  } = useFieldArray({ control, name: 'config.scenarioMappings' });

  return (
    <Box>
      <Stack spacing={6}>
        {/* File Format — only for CSV/Excel file source */}
        {isFileOnly && (
          <Box>
            <Typography variant='h6' gutterBottom>
              File Format
            </Typography>
            <Stack spacing={4}>
              <Controller
                name='config.fileLevel.sourceFormat'
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label='Source Format' fullWidth>
                    <MenuItem value='csv'>CSV</MenuItem>
                    <MenuItem value='excel'>Excel</MenuItem>
                  </TextField>
                )}
              />
              {sourceFormat === 'csv' && (
                <TextField
                  {...register('config.fileLevel.delimiter', {
                    required: sourceFormat === 'csv' ? 'Delimiter is required for CSV files' : false,
                    validate: (value) => {
                      if (sourceFormat === 'csv' && !value?.trim()) {
                        return 'Delimiter cannot be empty';
                      }
                      return true;
                    },
                  })}
                  label='Delimiter'
                  placeholder='e.g., , or | or ;'
                  fullWidth
                  required
                  // @ts-expect-error - Nested error access for deeply nested form fields
                  error={!!errors.config?.fileLevel?.delimiter}
                  // @ts-expect-error - Nested error access for deeply nested form fields
                  helperText={errors.config?.fileLevel?.delimiter?.message as string}
                  slotProps={{ htmlInput: { maxLength: 1 } }}
                />
              )}
              {isEntityProperties && <RowLayoutFields />}
            </Stack>
          </Box>
        )}

        {/* Row Layout — Snowflake × Entity Properties (file has it inside File Format above) */}
        {isSnowflake && isEntityProperties && (
          <Box>
            <Typography variant='h6' gutterBottom>
              Row Layout
            </Typography>
            <Stack spacing={4}>
              <RowLayoutFields />
            </Stack>
          </Box>
        )}

        {/* Source note — API integration (connection details live on Basic Info) */}
        {isApiIntegration && (
          <Box>
            <Typography variant='h6' gutterBottom>
              Source
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {integrationProvider
                ? `Data is read from the ${integrationProvider} integration staging tables. Entities are resolved automatically from ${integrationProvider} staging.`
                : 'Select an Integration Provider on the Basic Info tab. Entities are resolved automatically from the provider’s staging tables.'}
            </Typography>
          </Box>
        )}

        {/* Entity Mapping — shown for file and Snowflake sources */}
        {isFileOrSnowflake && (
          <Box>
            <Typography variant='h6' gutterBottom>
              {targetEntity === 'round'
                ? 'Round Mapping'
                : targetEntity === 'fundcompany'
                  ? 'Fund Company Mapping'
                  : 'Company Mapping'}
            </Typography>
            <Typography variant='body2' color='text.secondary' gutterBottom>
              {targetEntity === 'round'
                ? 'Each row is matched to the company that raised the round, then to a specific round by date.'
                : targetEntity === 'fundcompany'
                  ? 'Each row is matched to a company and a fund.'
                  : 'How each row is matched to a Foresight company.'}
            </Typography>
            <Stack spacing={4} mt={2}>
              <Controller
                name='config.entityMapping.targetEntity'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label='Target Entity'
                    fullWidth
                    helperText='What each row in this source represents.'
                  >
                    <MenuItem value='company'>Company</MenuItem>
                    <MenuItem value='round'>Round</MenuItem>
                    <MenuItem value='fundcompany'>Fund Company</MenuItem>
                  </TextField>
                )}
              />

              {isSnowflake ? (
                <Controller
                  name='config.entityMapping.identifierField'
                  control={control}
                  render={({ field }) => (
                    <SnowflakeColumnAutocomplete
                      label='Company Identifier Column'
                      required
                      field={field}
                      options={snowflakeColumns.options}
                      disabled={snowflakeColumns.disabled || snowflakeColumns.loading}
                      loading={snowflakeColumns.loading}
                      error={snowflakeColumns.error}
                      helperText={
                        !snowflakeColumns.hasTable
                          ? 'Select a Snowflake connection and table first.'
                          : targetEntity === 'round'
                            ? 'Column identifying the company that raised the round (e.g. COMPANY_EXTERNAL_ID).'
                            : 'Column whose value identifies the company (e.g. COMPANY_EXTERNAL_ID).'
                      }
                    />
                  )}
                />
              ) : (
                <TextField
                  {...register('config.entityMapping.identifierField')}
                  label='Company Identifier Column'
                  placeholder='e.g., Company Name'
                  helperText={
                    targetEntity === 'round'
                      ? 'Column identifying the company that raised the round.'
                      : "Column whose value identifies the company (company name, or the provider's external id when using Provider Lookup)."
                  }
                  fullWidth
                />
              )}

              <Controller
                name='config.entityMapping.lookupStrategy'
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label='Company Lookup Strategy' fullWidth>
                    <MenuItem value='name-lookup'>Name Lookup</MenuItem>
                    <MenuItem value='provider-lookup'>Provider Lookup</MenuItem>
                    <MenuItem value='id-lookup'>ID Lookup (numeric Foresight company id)</MenuItem>
                  </TextField>
                )}
              />

              {lookupStrategy === 'provider-lookup' && (
                <Controller
                  name='config.entityMapping.providerName'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      options={INTERNAL_PROVIDERS}
                      freeSolo
                      onChange={(_, value) => field.onChange(value || '')}
                      value={field.value || ''}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Provider Name'
                          placeholder='e.g., Investran, Carta'
                          helperText='The provider whose external ids appear in the identifier column.'
                        />
                      )}
                      fullWidth
                    />
                  )}
                />
              )}

              {targetEntity === 'round' && (
                <>
                  <Typography variant='subtitle2'>Round matching</Typography>
                  <TextField
                    {...register('config.entityMapping.dateField')}
                    label='Date Field'
                    placeholder='e.g., RoundDate, InvestmentDate'
                    helperText='Column holding the round date used to match or create the round.'
                    fullWidth
                  />
                  <Controller
                    name='config.entityMapping.createIfNotFound'
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value ?? true}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label='Create round if not found'
                      />
                    )}
                  />
                  <TextField
                    {...register('config.entityMapping.dateTolerance', {
                      valueAsNumber: true,
                      min: { value: 0, message: 'Must be 0 or greater' },
                      validate: (value) => !value || Number.isInteger(value) || 'Must be a whole number',
                    })}
                    label='Date Tolerance (days)'
                    type='number'
                    placeholder='0 = exact match'
                    helperText='Days the date can differ and still match an existing round (0 = exact).'
                    fullWidth
                    slotProps={{ htmlInput: { min: 0, step: 1 } }}
                  />
                </>
              )}

              {targetEntity === 'fundcompany' && (
                <TextField
                  {...register('config.entityMapping.fundIdentifierField')}
                  label='Fund Identifier Column'
                  placeholder='e.g., Fund Name'
                  helperText='Column identifying the fund; combined with the company to resolve the fund-company holding.'
                  fullWidth
                />
              )}
            </Stack>
          </Box>
        )}

        {/* Date & Period — ONE layout for every source (FD-3697): the same three rows
            (Date / Period Type / Budget vs Actual) in the same order; only the widget
            inside each row varies (text input, schema autocomplete, or a read-only
            "derived" field). For file × Entity Properties the section carries a single
            optional row: the effective-date column for date-based (time-series) custom
            fields (config.customFieldMetadata.dateColumn — file runner only). */}
        {(hasPeriod || (isFileOnly && isEntityProperties)) && (
          <Box>
            <Typography variant='h6' gutterBottom>
              Date &amp; Period
            </Typography>
            <Typography variant='body2' color='text.secondary' gutterBottom>
              {hasPeriod
                ? 'Where each row’s date and reporting period come from.'
                : 'Optional — where date-based custom field values get their effective date.'}
            </Typography>
            <Stack spacing={4} mt={2}>
              {/* Row 0 — effective date for date-based custom fields (file Entity Properties) */}
              {isFileOnly && isEntityProperties && (
                <TextField
                  {...register('config.customFieldMetadata.dateColumn')}
                  label='Custom Field Date Column (optional)'
                  placeholder='e.g., AsOfDate, EffectiveDate'
                  helperText='Applies only to date-based (time-series) custom fields; other fields keep a single value. Leave empty to keep the current single-value behavior.'
                  fullWidth
                />
              )}

              {/* Row 1 — Date / Reporting Period */}
              {isFileOnly && hasPeriod && (
                <TextField
                  {...register('config.fileLevel.kpiMetadata.dateColumn')}
                  label='Date Column'
                  placeholder='e.g., AsOfDate, Date, ReportDate'
                  helperText='Required. Column holding the as-of date for each row.'
                  fullWidth
                />
              )}
              {isSnowflake && (
                <>
                  <Controller
                    name='config.snowflakeConfig.reportingPeriodColumn'
                    control={control}
                    render={({ field }) => (
                      <SnowflakeColumnAutocomplete
                        label='Reporting Period Column'
                        required
                        field={field}
                        options={snowflakeColumns.options}
                        disabled={snowflakeColumns.disabled || snowflakeColumns.loading}
                        loading={snowflakeColumns.loading}
                        error={snowflakeColumns.error}
                        helperText='Holds the reporting period for each row — required for KPI / commentary writes.'
                      />
                    )}
                  />
                  <Controller
                    name='config.snowflakeConfig.reportingPeriodSecondaryColumn'
                    control={control}
                    render={({ field }) => (
                      <SnowflakeColumnAutocomplete
                        label='Reporting Period — secondary column (optional)'
                        field={field}
                        options={snowflakeColumns.options}
                        disabled={snowflakeColumns.disabled || snowflakeColumns.loading}
                        loading={snowflakeColumns.loading}
                        error={snowflakeColumns.error}
                        helperText='Use when the period is split across two columns, e.g. REPORTING_YEAR + REPORTING_QUARTER.'
                      />
                    )}
                  />
                </>
              )}
              {isApiIntegration && (
                <TextField
                  label='Date'
                  value='Derived from the provider’s staging data (period end date).'
                  disabled
                  fullWidth
                />
              )}

              {/* Row 2 — Period Type */}
              {isFileOnly && hasPeriod && (
                <TextField
                  {...register('config.fileLevel.kpiMetadata.periodTypeColumn')}
                  label='Period Type Column'
                  placeholder='e.g., PeriodType (Yearly, Quarterly, Monthly)'
                  helperText='Optional. Column holding the period length; map its values below.'
                  fullWidth
                />
              )}
              {isSnowflake && (
                <TextField
                  label='Period Type'
                  value='Derived from the Reporting Period Column format.'
                  disabled
                  fullWidth
                />
              )}
              {isApiIntegration && (
                <TextField
                  label='Period Type'
                  value='Derived from the provider’s staging data (period length). Map its values below.'
                  disabled
                  fullWidth
                />
              )}

              {/* Row 3 — Budget vs Actual (Company Financials only) */}
              {isFinancials && isFileOnly && (
                <TextField
                  {...register('config.fileLevel.kpiMetadata.sectionColumn')}
                  label='Budget vs Actual Column'
                  placeholder='e.g., Section (ACTUAL or BUDGET)'
                  helperText='Optional. Column holding the scenario; map its values below.'
                  fullWidth
                />
              )}
              {isFinancials && isSnowflake && (
                <TextField
                  label='Budget vs Actual'
                  value='Not yet supported for Snowflake sources — values load as Actual.'
                  disabled
                  fullWidth
                />
              )}
              {isFinancials && isApiIntegration && (
                <TextField
                  label='Budget vs Actual'
                  value='Derived from the provider’s staging data (scenario). Map its values below.'
                  disabled
                  fullWidth
                />
              )}

              {/* Row 4 — Budget ID (Standard Metrics financials only). Scopes which
                  budget's rows feed the Budget section; the sync leaves other budgets'
                  rows PENDING when set. */}
              {isFinancials && isApiIntegration && integrationProvider === 'StandardMetrics' && (
                <TextField
                  {...register('config.budgetId')}
                  label='Budget ID (optional)'
                  placeholder='e.g., bdg_1a2b3c'
                  helperText='Matched against Standard Metrics’ budget_id on staged rows. Only Budget-section rows are scoped; leave empty to load every budget.'
                  fullWidth
                />
              )}
            </Stack>
          </Box>
        )}

        {/* Detection Rules — file source only */}
        {isFileOnly && (
          <Box>
            <Typography variant='h6' gutterBottom>
              Detection Rules
            </Typography>
            <Typography variant='body2' color='text.secondary' gutterBottom>
              File type detection is automatically based on the columns configured in your field mappings.
              Optionally add filename or S3 path patterns for additional matching.
            </Typography>

            {/* Filename Patterns */}
            <Box mt={3}>
              <Typography variant='subtitle2' gutterBottom>
                Filename Patterns
              </Typography>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                Regex patterns to match filenames (e.g., board.*\.csv)
              </Typography>
              <Stack spacing={2} mt={2}>
                {filenameFields.map((field, index) => (
                  <Stack key={field.id} direction='row' spacing={1} alignItems='center'>
                    <TextField
                      {...register(`config.detection.filenamePatterns.${index}`)}
                      placeholder='e.g., board_directors.*'
                      fullWidth
                      size='small'
                    />
                    <IconButton onClick={() => removeFilename(index)} size='small'>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => appendFilename('')}
                  variant='outlined'
                  size='small'
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add Pattern
                </Button>
              </Stack>
            </Box>

            {/* S3 Path Patterns */}
            <Box mt={3}>
              <Typography variant='subtitle2' gutterBottom>
                S3 Path Patterns
              </Typography>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                S3 path patterns to match file locations
              </Typography>
              <Stack spacing={2} mt={2}>
                {s3PathFields.map((field, index) => (
                  <Stack key={field.id} direction='row' spacing={1} alignItems='center'>
                    <TextField
                      {...register(`config.detection.s3PathPatterns.${index}`)}
                      placeholder='e.g., uploads/board/*'
                      fullWidth
                      size='small'
                    />
                    <IconButton onClick={() => removeS3Path(index)} size='small'>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => appendS3Path('')}
                  variant='outlined'
                  size='small'
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add Pattern
                </Button>
              </Stack>
            </Box>
          </Box>
        )}

        {/* Period Length Mapping — periodic data only (translates source periods → KPI periods) */}
        {hasPeriod && (
          <Box>
            <Typography variant='h6' gutterBottom>
              Period Length Mapping
            </Typography>
            <Typography variant='body2' color='text.secondary' gutterBottom>
              Map source period values to Foresight KPI periods.
            </Typography>

            {periodFields.length > 0 && (
              <Stack direction='row' spacing={1} sx={{ mb: 1, px: 0.5, mt: 2 }}>
                <Typography variant='caption' sx={{ flex: 1 }}>
                  Source Period
                </Typography>
                <Typography variant='caption' sx={{ flex: 1 }}>
                  KPI Period
                </Typography>
                <Box sx={{ width: 32 }} />
              </Stack>
            )}

            <Stack spacing={1} mt={periodFields.length > 0 ? 0 : 2}>
              {periodFields.map((field, index) => (
                <Stack key={field.id} direction='row' spacing={1} alignItems='center'>
                  {isApiIntegration ? (
                    <Controller
                      name={`config.periodLengthMappings.${index}.source`}
                      control={control}
                      render={({ field: f }) => (
                        <TextField {...f} select label='Source Period' size='small' sx={{ flex: 1 }}>
                          {(distinctValues?.periodLengths ?? []).map((pl) => (
                            <MenuItem key={pl} value={pl}>
                              {pl}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  ) : (
                    <TextField
                      {...register(`config.periodLengthMappings.${index}.source`)}
                      label='Source Period'
                      placeholder='e.g., Quarterly, Monthly, LTM'
                      size='small'
                      sx={{ flex: 1 }}
                    />
                  )}
                  <Controller
                    name={`config.periodLengthMappings.${index}.target`}
                    control={control}
                    render={({ field: f }) => (
                      <TextField {...f} select label='KPI Period' size='small' sx={{ flex: 1 }}>
                        {KPI_PERIODS.map((p) => (
                          <MenuItem key={p.value} value={p.value}>
                            {p.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  <IconButton onClick={() => removePeriod(index)} size='small'>
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Stack>
              ))}
            </Stack>

            <Button
              startIcon={<AddIcon />}
              onClick={() => appendPeriod({ source: '', target: '' })}
              variant='outlined'
              size='small'
              sx={{ mt: 2 }}
            >
              Add Period Mapping
            </Button>
          </Box>
        )}

        {/* Scenario Mapping — financials only (Budget vs Actual has no meaning elsewhere) */}
        {isFinancials && (
          <Box>
            <Typography variant='h6' gutterBottom>
              Scenario Mapping
            </Typography>
            <Typography variant='body2' color='text.secondary' gutterBottom>
              Map source scenario values to Foresight KPI sections.
            </Typography>

            {scenarioFields.length > 0 && (
              <Stack direction='row' spacing={1} sx={{ mb: 1, px: 0.5, mt: 2 }}>
                <Typography variant='caption' sx={{ flex: 1 }}>
                  Source Scenario
                </Typography>
                <Typography variant='caption' sx={{ flex: 1 }}>
                  KPI Section
                </Typography>
                <Box sx={{ width: 32 }} />
              </Stack>
            )}

            <Stack spacing={1} mt={scenarioFields.length > 0 ? 0 : 2}>
              {scenarioFields.map((field, index) => (
                <Stack key={field.id} direction='row' spacing={1} alignItems='center'>
                  {isApiIntegration ? (
                    <Controller
                      name={`config.scenarioMappings.${index}.source`}
                      control={control}
                      render={({ field: f }) => (
                        <TextField {...f} select label='Source Scenario' size='small' sx={{ flex: 1 }}>
                          {(distinctValues?.scenarios ?? []).map((s) => (
                            <MenuItem key={s} value={s}>
                              {s}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  ) : (
                    <TextField
                      {...register(`config.scenarioMappings.${index}.source`)}
                      label='Source Scenario'
                      placeholder='e.g., Actual, Budget, Forecast'
                      size='small'
                      sx={{ flex: 1 }}
                    />
                  )}
                  <Controller
                    name={`config.scenarioMappings.${index}.target`}
                    control={control}
                    render={({ field: f }) => (
                      <TextField {...f} select label='KPI Section' size='small' sx={{ flex: 1 }}>
                        {KPI_SECTIONS.map((s) => (
                          <MenuItem key={s.value} value={s.value}>
                            {s.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  <IconButton onClick={() => removeScenario(index)} size='small'>
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Stack>
              ))}
            </Stack>

            <Button
              startIcon={<AddIcon />}
              onClick={() => appendScenario({ source: '', target: '' })}
              variant='outlined'
              size='small'
              sx={{ mt: 2 }}
            >
              Add Scenario Mapping
            </Button>
          </Box>
        )}

        {/* Upsert Strategy (shared for both source types) */}
        <Box>
          <Typography variant='h6' gutterBottom>
            Sync Behavior
          </Typography>
          <Typography variant='body2' color='text.secondary' gutterBottom>
            Control how existing data is handled when new values arrive.
          </Typography>
          {isSnowflake && (
            <Box mt={2}>
              <Controller
                name='config.snowflakeConfig.sourceTimestampColumn'
                control={control}
                render={({ field }) => (
                  <SnowflakeColumnAutocomplete
                    label='Source Last-Modified Column (optional)'
                    field={field}
                    options={snowflakeColumns.options}
                    disabled={snowflakeColumns.disabled || snowflakeColumns.loading}
                    loading={snowflakeColumns.loading}
                    error={snowflakeColumns.error}
                    helperText='Optional row-level timestamp from Snowflake. Enables newer-wins upserts.'
                  />
                )}
              />
            </Box>
          )}
          <Box mt={2}>
            <Controller
              name='config.upsertStrategy'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  select
                  label='Upsert Strategy'
                  fullWidth
                  onChange={(e) => field.onChange(e.target.value || undefined)}
                >
                  <MenuItem value=''>None (create only, no upsert)</MenuItem>
                  <MenuItem value='replace-if-newer'>Replace if source is newer</MenuItem>
                  <MenuItem value='always'>Always overwrite</MenuItem>
                  <MenuItem value='only-if-empty'>Only if empty / null / zero</MenuItem>
                  <MenuItem value='insert-only'>Insert only (never update)</MenuItem>
                </TextField>
              )}
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}

// Backwards compat alias
export { IngestionSettingsSection as FileSettingsSection };
